import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Receipt, ShoppingCart, Minus, Plus, Trash2, CheckCircle2, CreditCard, FileText, Tag, X, Sparkles, Search, UserPlus, Loader2, Phone, User, Crown, TrendingUp, Award, Settings, ChevronDown, ChevronUp, Printer, Zap, Globe } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { SwipeableRow } from './SwipeableRow';
import { openNumpad as globalOpenNumpad } from './Numpad';
import { getAgentConfig } from '../../utils/thermalPrint';
import { ReceiptTemplateRenderer, type ReceiptRendererData } from './ReceiptTemplateRenderer';

interface Product {
  id: number;
  name: string;
  price: number;
  emoji?: string;
  unitType: string;
  coreCharge?: number;
  category?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
}

interface CartItem {
  product: Product;
  qty: number;
  itemDiscount?: number;
  itemMarkup?: number;
  damageFlag?: boolean;
  adjustedPrice?: number;
  warrantyPeriod?: string;
  returnPolicy?: string;
  originalPrice?: number;
  priceLabel?: string;
}

interface LastTransaction {
  id: string;
  storeName: string;
  storeLogo?: string;
  storeAddress?: string;
  storePhone?: string;
  time: Date;
  cart: CartItem[];
  subtotal: number;
  discountPct: number;
  discountAmt: number;
  total: number;
  payments?: any[];
  method?: string;
  cashierName?: string;
  itemCount?: number;
  customer?: {
    id?: number;
    name?: string;
    phone?: string;
    address?: string;
    customFieldValue?: string;
    points?: number;
  } | null;
  pointsEarned?: number;
  creditAmount?: number;
  creditPromiseDate?: string | null;
}

interface AiSuggestion {
  item: string;
  reason: string;
}

interface CartSidebarProps {
  // Cart state
  cart: CartItem[];
  lastTransaction: LastTransaction | null;
  
  // Cashier list & selection
  cashiersList: any[];
  selectedCashier: { id: number; name: string } | null;
  setSelectedCashier: (cashier: { id: number; name: string } | null) => void;
  cashierName: string;
  
  // Totals
  subtotal: number;
  discount: number;
  discountAmt: number;
  coreChargesTotal: number;
  grandTotal: number;
  itemCount: number;
  
  // Settings
  storeName: string;
  now: Date;
  isCashierMode: boolean;
  sidebarPayMode: boolean;
  
  // AI & Suggestions
  aiSuggestion: AiSuggestion | null;
  dismissedSuggestions: Set<string>;
  
  // Callbacks
  updateQty: (productId: number, delta: number) => void;
  removeFromCart: (item: CartItem) => void;
  handleCheckout: () => void;
  setLastTransaction: (tx: LastTransaction | null) => void;
  setCart: (cart: CartItem[]) => void;
  setDiscount: (discount: number) => void;
  setShowDiscount: (show: boolean) => void;
  setSidebarPayMode: (mode: boolean) => void;
  setCashAmount: (amount: string) => void;
  setMobileProvider: (provider: string) => void;
  setDismissedSuggestions: (updater: (prev: Set<string>) => Set<string>) => void;
  setAiSuggestion: (suggestion: AiSuggestion | null) => void;
  handleAddSuggestedItem: (itemName: string) => void;
  isProcessing: boolean;
  handlePaymentConfirm: (method: string, amount: number, provider?: string) => void;
  onSaveEstimate?: () => void;
  updateItemDiscount?: (productId: number, discountAmt: number) => void;
  updateItemMarkup?: (productId: number, markupAmt: number) => void;
  toggleDamageFlag?: (productId: number) => void;
  updateItemSellPrice?: (productId: number, newPrice: number | null) => void;
  openNumpad?: (opts: any) => Promise<string | null>;
  getItemSellPrice?: (item: CartItem) => number;
  editingPolicyId?: number | null;
  openPolicyEditor?: (productId: number, currentReturnPolicy?: string, currentWarrantyPeriod?: string) => void;
  confirmPolicyEdit?: (productId: number) => void;
  editReturnPolicy?: string;
  setEditReturnPolicy?: (val: string) => void;
  editWarrantyPeriod?: string;
  setEditWarrantyPeriod?: (val: string) => void;
  updateCartItemPolicy?: (productId: number, updates: { returnPolicy?: string; warrantyPeriod?: string }) => void;
  
  // Helpers
  formatRs: (amount: number) => string;
  getUnitLabel: (product: Product) => string;
  isWeightVolume: (product: Product) => boolean;
  Haptics: any;
  customerPillJsx: React.ReactNode;
  PaymentMethodPicker: React.ComponentType<any>;
  customer?: any;
  storeApiKey?: string;
  onCustomerAttached?: (customer: any) => void;
  customFieldLabel?: string;
  // 💰 Points redemption
  loyaltyConfig?: { enabled: boolean; pointsPerRupee: number; rupeePerPoint: number };
  pointsToRedeem?: number;
  setPointsToRedeem?: (n: number) => void;
  totalBeforeRedemption?: number;
  redemptionValue?: number;
  estimatedPointsEarn?: number;
  // 💰 Store Credit
  storeCreditToUse?: number;
  setStoreCreditToUse?: (n: number) => void;
  maxStoreCredit?: number;
  effectiveStoreCredit?: number;
  // 🖨️ Auto Print Bill toggle
  autoPrintBill?: boolean;
  onToggleAutoPrint?: () => void;
  // 🧾 Show Kangavena logo on bill
  showLogoOnBill?: boolean;
  // 🧾 Disable "Powered by Kangavena" on bill
  hidePoweredByOnBill?: boolean;
  // 🖨️ Direct thermal reprint callback
  onPrintThermalReceipt?: () => void;
  // 🧾 Active receipt template
  receiptTemplate?: string;
}

export const CartSidebar: React.FC<CartSidebarProps> = ({
  cart,
  lastTransaction,
  cashiersList,
  selectedCashier,
  setSelectedCashier,
  cashierName,
  subtotal,
  discount,
  discountAmt,
  coreChargesTotal,
  grandTotal,
  itemCount,
  storeName,
  now,
  isCashierMode,
  sidebarPayMode,
  aiSuggestion,
  dismissedSuggestions,
  updateQty,
  removeFromCart,
  handleCheckout,
  setLastTransaction,
  setCart,
  setDiscount,
  setShowDiscount,
  setSidebarPayMode,
  setCashAmount,
  setMobileProvider,
  setDismissedSuggestions,
  setAiSuggestion,
  handleAddSuggestedItem,
  isProcessing,
  handlePaymentConfirm,
  onSaveEstimate,
  updateItemDiscount,
  updateItemMarkup,
  toggleDamageFlag,
  updateItemSellPrice,
  openNumpad: propOpenNumpad,
  getItemSellPrice,
  editingPolicyId,
  openPolicyEditor,
  confirmPolicyEdit,
  editReturnPolicy,
  setEditReturnPolicy,
  editWarrantyPeriod,
  setEditWarrantyPeriod,
  formatRs,
  getUnitLabel,
  isWeightVolume,
  Haptics,
  customerPillJsx,
  PaymentMethodPicker,
  customer,
  storeApiKey,
  onCustomerAttached,
  customFieldLabel = 'Vehicle Number',
  // 💰 Points redemption
  loyaltyConfig = { enabled: false, pointsPerRupee: 0.01, rupeePerPoint: 0.5 },
  pointsToRedeem = 0,
  setPointsToRedeem,
  totalBeforeRedemption = 0,
  redemptionValue = 0,
  estimatedPointsEarn = 0,
  // 💰 Store Credit
  storeCreditToUse = 0,
  setStoreCreditToUse,
  maxStoreCredit = 0,
  effectiveStoreCredit = 0,
  // 🖨️ Auto Print Bill toggle
  autoPrintBill = false,
  onToggleAutoPrint,
  // 🧾 Show Kangavena logo on bill
  showLogoOnBill = false,
  // 🧾 Disable "Powered by Kangavena" on bill
  hidePoweredByOnBill = false,
  // 🖨️ Direct thermal reprint callback
  onPrintThermalReceipt,
  // 🧾 Active receipt template
  receiptTemplate = 'modern_left_brand',
}) => {
  // ── Reprint selection modal state ──
  const [showPrintChoiceModal, setShowPrintChoiceModal] = useState(false);
  // ── Inline customer search state ──
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerTopCustomers, setCustomerTopCustomers] = useState<any[]>([]);
  const [customerTopLoading, setCustomerTopLoading] = useState(false);
  const [customerCreateMode, setCustomerCreateMode] = useState(false);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', address: '', customFieldValue: '' });
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [isSuggestionCollapsed, setIsSuggestionCollapsed] = useState(true);
  const debounceRef = useRef<number | null>(null);

  const getCustHeaders = () => {
    const token = localStorage.getItem('kangavena:cashierToken') || '';
    return {
      'x-store-api-key': storeApiKey || '',
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const looksLikePhone = (s: string) => /^[+0-9][0-9\s-]{5,}$/.test(s.trim());

  const submitNewCustomer = async () => {
    setCustomerError('');
    if (!customerForm.name.trim() || !customerForm.phone.trim()) {
      setCustomerError('Name and phone are required');
      return;
    }
    setCustomerSubmitting(true);
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: getCustHeaders(),
        body: JSON.stringify(customerForm),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.customer) {
          onCustomerAttached?.(data.customer);
          setCustomerSearchOpen(false);
          resetCustomerSearch();
          return;
        }
        setCustomerError(data.error || 'Failed to create customer');
        return;
      }
      onCustomerAttached?.(data.customer);
      setCustomerSearchOpen(false);
      resetCustomerSearch();
    } catch (err: any) {
      setCustomerError(err?.message || 'Network error');
    } finally {
      setCustomerSubmitting(false);
    }
  };

  const resetCustomerSearch = () => {
    setCustomerQuery('');
    setCustomerResults([]);
    setCustomerTopCustomers([]);
    setCustomerCreateMode(false);
    setCustomerForm({ name: '', phone: '', address: '', customFieldValue: '' });
    setCustomerError('');
  };

  // Debounced customer search
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      const q = customerQuery.trim();
      if (!q) { setCustomerResults([]); setCustomerLoading(false); return; }
      setCustomerLoading(true);
      try {
        const res = await fetch(`/api/customers?q=${encodeURIComponent(q)}&sort=spend&limit=10`, { headers: getCustHeaders() });
        const data = await res.json();
        if (res.ok) setCustomerResults(data.customers || []);
      } catch { /* network — silent */ }
      finally { setCustomerLoading(false); }
    }, 250);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [customerQuery, storeApiKey]);

  // 🔥 Top customers — inline suggestions when search panel opens
  useEffect(() => {
    if (!customerSearchOpen || customerQuery.trim() || customerCreateMode) {
      setCustomerTopCustomers([]);
      return;
    }
    setCustomerTopLoading(true);
    fetch(`/api/customers?sort=spend&limit=5`, { headers: getCustHeaders() })
      .then((r) => r.json())
      .then((data) => setCustomerTopCustomers(data.customers || []))
      .catch(() => { /* silent */ })
      .finally(() => setCustomerTopLoading(false));
  }, [customerSearchOpen, customerQuery, customerCreateMode, storeApiKey]);

  return (
    <div className="hidden md:flex print:block w-72 lg:w-80 xl:w-96 flex-col bg-white/90 backdrop-blur-2xl border-l border-slate-100 shrink-0">
      {/* Bill header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100/80 shrink-0 print:hidden" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#1e1b4b]/10 to-brand-primary/10 border border-[#1e1b4b]/15 flex items-center justify-center">
            <Receipt size={11} className="text-[#1e1b4b]" />
          </div>
          <span className="font-black text-[10px] text-[#1e1b4b] uppercase tracking-[0.15em]">Current Bill</span>
          {/* 🖨️ Auto Print toggle — in bill header */}
          {onToggleAutoPrint && (
            <button
              onClick={onToggleAutoPrint}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider transition-all border ${
                autoPrintBill
                  ? 'bg-violet-500/10 border-violet-500/20 text-violet-600'
                  : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
              }`}
              title={autoPrintBill ? 'Auto Print ON' : 'Auto Print OFF'}
            >
              🖨️ {autoPrintBill ? 'Auto' : 'Manual'}
            </button>
          )}
        </div>
        {itemCount > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-[#059669]/10 text-[#059669] text-[8px] font-bold border border-[#059669]/20">
            {itemCount} item{itemCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Cart items OR Receipt */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 print:overflow-visible print:px-0 print:py-0">
        <AnimatePresence>
          {lastTransaction ? (
            /* 🧾 PROFESSIONAL RECEIPT - Desktop (screen) — 100% WYSIWYG parity with thermal printout */
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center py-2 print:hidden w-full">
              <div className="w-full rounded-xl overflow-hidden shadow-lg border border-slate-200 bg-white">
                <ReceiptTemplateRenderer
                  templateId={receiptTemplate}
                  data={{
                    id: lastTransaction.id,
                    storeName: storeName || lastTransaction.storeName,
                    storeLogo: lastTransaction.storeLogo,
                    storeAddress: lastTransaction.storeAddress,
                    storePhone: lastTransaction.storePhone,
                    time: lastTransaction.time,
                    cashierName: cashierName || lastTransaction.cashierName,
                    customer: lastTransaction.customer,
                    cart: lastTransaction.cart,
                    subtotal: lastTransaction.subtotal,
                    discountPct: lastTransaction.discountPct,
                    discountAmt: lastTransaction.discountAmt,
                    storeCreditUsed: lastTransaction.storeCreditUsed,
                    total: lastTransaction.total,
                    payments: lastTransaction.payments,
                    change: (lastTransaction.payments || []).find((p: any) => p.change !== undefined)?.change || lastTransaction.change || 0,
                  }}
                  is4Inch={/(470|4-inch|4inch|104|108|100|label|barcode)/i.test(getAgentConfig().defaultPrinter || '')}
                  showLogoOnBill={showLogoOnBill}
                  hidePoweredByOnBill={hidePoweredByOnBill}
                />
              </div>
            </motion.div>
          ) : cart.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="flex flex-col items-center justify-center py-10 gap-3 print:hidden"
            >
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(6,182,212,0.08))', border: '1px solid rgba(16,185,129,0.15)' }}
              >
                <ShoppingCart size={26} className="text-emerald-400/50" strokeWidth={1.5} />
              </motion.div>
              <div className="text-center space-y-1">
                <p className="text-[11px] font-bold text-slate-500">Bill is empty</p>
                <p className="text-[9px] text-slate-400 leading-relaxed max-w-[180px]">
                  Tap a product to start · Scan a barcode · Press <kbd className="px-1 py-0.5 text-[8px] bg-slate-100 border border-slate-200 rounded font-mono text-slate-500">K</kbd> for shortcuts
                </p>
              </div>
            </motion.div>
          ) : cart.map((item, idx) => {
            const effectivePrice = getItemSellPrice ? getItemSellPrice(item) : item.product.price;
            const hasAdjustment = (item.itemDiscount && item.itemDiscount > 0) || (item.itemMarkup && item.itemMarkup > 0) || (item.adjustedPrice !== undefined && item.adjustedPrice !== null);
            
            return (
            <SwipeableRow key={`${item.product.id}-${item.priceLabel || ''}-${idx}`} onDelete={() => removeFromCart(item)} className="print:hidden">
              <div className="flex flex-col gap-1.5 p-2 rounded-xl bg-slate-50/90 border border-slate-200/80 hover:border-slate-300 transition-colors shadow-xs">
                {/* Product Row */}
                <div className="flex items-center gap-1.5">
                  <span className="text-sm shrink-0">{item.product.emoji || '📦'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <p className="text-[10.5px] font-bold text-slate-800 truncate leading-snug">{item.product.name}</p>
                      {item.priceLabel && (
                        <span className="px-1 py-0.2 rounded bg-emerald-500/10 text-emerald-700 text-[7.5px] font-black uppercase tracking-wider border border-emerald-500/20">
                          {item.priceLabel}
                        </span>
                      )}
                      <button
                        onClick={() => openPolicyEditor?.(item.product.id, item.returnPolicy, item.warrantyPeriod)}
                        className="shrink-0 p-0.5 rounded hover:bg-slate-200 cursor-pointer transition-colors"
                        title="Override warranty & return policy"
                      >
                        <Settings size={13} className="text-slate-400 hover:text-slate-700" />
                      </button>
                    </div>
                    {(item.returnPolicy || item.warrantyPeriod) && editingPolicyId !== item.product.id ? (
                      <p className="text-[8.5px] text-slate-500 italic leading-none mt-0.5">{[item.returnPolicy, item.warrantyPeriod ? `Warranty: ${item.warrantyPeriod}` : null].filter(Boolean).join(" | ")}</p>
                    ) : null}
                    <p className="text-[8px] text-slate-500 font-mono mt-0.5 leading-tight">
                      {hasAdjustment ? (
                        <>
                          <span className="line-through text-slate-400">{formatRs(item.product.price)}</span>
                          {item.itemDiscount && item.itemDiscount > 0 && <span className="ml-1 text-rose-600 font-semibold">-{formatRs(item.itemDiscount)}</span>}
                          {item.itemMarkup && item.itemMarkup > 0 && <span className="ml-1 text-emerald-600 font-semibold">+{formatRs(item.itemMarkup)}</span>}
                          {item.adjustedPrice !== undefined && item.adjustedPrice !== null && <span className="ml-1 text-blue-600 font-bold">={formatRs(item.adjustedPrice)}</span>}
                          <span className="ml-1 text-slate-800 font-black">→ {formatRs(effectivePrice)}</span>
                        </>
                      ) : (
                        formatRs(item.product.price)
                      )}
                      {getUnitLabel(item.product) ? `/${item.product.unitType}` : ''} × {isWeightVolume(item.product) ? ((item.product.unitType === 'ml' || item.product.unitType === 'Grams') ? Math.round(item.qty) + item.product.unitType : item.qty.toFixed(1) + item.product.unitType) : item.qty} = <span className="text-slate-900 font-black">{formatRs(effectivePrice * item.qty)}</span>
                    </p>
                    {(item.product.minPrice != null || item.product.maxPrice != null) && (
                      <span className="inline-block px-1 py-0.2 mt-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200/80 text-[7px] font-extrabold font-mono tracking-tight leading-none">
                        ↔️ Allowed: {formatRs(item.product.minPrice || 0)} - {formatRs(item.product.maxPrice || 0)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => updateQty(item.product.id, -1)} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 hover:text-slate-800 transition-colors">
                      <Minus size={11} />
                    </button>
                    <span className="w-6 text-center text-[10px] font-black text-slate-800 font-mono">{isWeightVolume(item.product) ? ((item.product.unitType === 'ml' || item.product.unitType === 'Grams') ? Math.round(item.qty) : item.qty.toFixed(1)) : item.qty}</span>
                    <button onClick={() => updateQty(item.product.id, 1)} className="w-6 h-6 rounded-md bg-brand-surface hover:bg-brand-primary-100 border border-brand-surface-border flex items-center justify-center text-brand-primary transition-colors">
                      <Plus size={11} />
                    </button>
                    <button onClick={() => removeFromCart(item)} className="w-6 h-6 rounded-md bg-rose-100 hover:bg-rose-200 border border-rose-200 hover:border-rose-300 flex items-center justify-center text-rose-500 hover:text-rose-600 transition-all ml-0.5">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                {/* 🔧 Policy Editor — row below product */}
                {editingPolicyId === item.product.id && (
                  <div className="flex flex-col gap-1.5 w-full p-2 bg-slate-800 rounded border border-slate-600">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[8.5px] text-slate-400 font-mono uppercase tracking-wider">Return Policy</label>
                      <input
                        type="text"
                        value={editReturnPolicy || ''}
                        onChange={(e) => setEditReturnPolicy?.(e.target.value)}
                        placeholder="e.g. 14 Days, No Return, Standard"
                        className="h-7 w-full text-xs rounded border-2 border-slate-500 bg-slate-900 text-white px-2 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[8.5px] text-slate-400 font-mono uppercase tracking-wider">Warranty Period</label>
                      <input
                        type="text"
                        value={editWarrantyPeriod || ''}
                        onChange={(e) => setEditWarrantyPeriod?.(e.target.value)}
                        placeholder="e.g. 6 Months, 1 Year, No Warranty"
                        className="h-7 w-full text-xs rounded border-2 border-slate-500 bg-slate-900 text-white px-2 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                    <button
                      onClick={() => confirmPolicyEdit?.(item.product.id)}
                      className="w-full h-7 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1 cursor-pointer transition-colors"
                    >✓ Confirm</button>
                  </div>
                )}

                {/* Inline Controls Row — single neat flex row without orphan wrapping */}
                <div className="flex items-center justify-between gap-1 pt-1 mt-0.5 border-t border-slate-200/60">
                  <div className="flex items-center gap-1">
                    {/* Damage Flag */}
                    {toggleDamageFlag && (
                      <button
                        type="button"
                        onClick={() => toggleDamageFlag(item.product.id)}
                        className={`px-1.5 py-0.5 text-[8px] font-semibold rounded border transition-all ${
                          item.damageFlag
                            ? 'bg-amber-100 border-amber-300 text-amber-800'
                            : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200/60'
                        }`}
                      >
                        {item.damageFlag ? '⚠️ Dmg' : '🏷️ Dmg?'}
                      </button>
                    )}

                    {/* Discount Input */}
                    {updateItemDiscount && (
                      <div className="flex items-center gap-0.5 bg-rose-50/60 px-1 py-0.5 rounded border border-rose-200/70">
                        <span className="text-[8px] text-rose-600 font-bold">−</span>
                        <input
                          type="number"
                          value={item.itemDiscount || ''}
                          onChange={(e) => updateItemDiscount(item.product.id, parseFloat(e.target.value) || 0)}
                          className="w-9 h-4 text-[8.5px] text-center font-mono rounded bg-white border border-rose-300 focus:border-rose-500 focus:outline-none p-0"
                          placeholder="0"
                          min="0"
                          step="10"
                        />
                        <span className="text-[7.5px] text-rose-500 font-medium">disc</span>
                      </div>
                    )}

                    {/* Markup Input */}
                    {updateItemMarkup && (
                      <div className="flex items-center gap-0.5 bg-emerald-50/60 px-1 py-0.5 rounded border border-emerald-200/70">
                        <span className="text-[8px] text-emerald-600 font-bold">+</span>
                        <input
                          type="number"
                          value={item.itemMarkup || ''}
                          onChange={(e) => updateItemMarkup(item.product.id, parseFloat(e.target.value) || 0)}
                          className="w-9 h-4 text-[8.5px] text-center font-mono rounded bg-white border border-emerald-300 focus:border-emerald-500 focus:outline-none p-0"
                          placeholder="0"
                          min="0"
                          step="10"
                        />
                        <span className="text-[7.5px] text-emerald-500 font-medium">markup</span>
                      </div>
                    )}
                  </div>

                  {/* Price Override button */}
                  {updateItemSellPrice && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={async () => {
                          const currentVal = item.adjustedPrice !== undefined && item.adjustedPrice !== null
                            ? item.adjustedPrice.toString()
                            : item.product.price.toString();
                          let val: string | null = null;
                          const numpadFn = propOpenNumpad || globalOpenNumpad;
                          try {
                            val = await numpadFn({
                              title: `Set price: ${item.product.name}`,
                              initial: currentVal,
                              suffix: ' Rs.',
                              allowDecimal: true,
                            });
                          } catch {
                            val = prompt(`Set price for ${item.product.name}:`, currentVal);
                          }
                          if (val === null) return;
                          if (val.trim() === '') {
                            updateItemSellPrice(item.product.id, null);
                            return;
                          }
                          const parsed = parseFloat(val);
                          if (isNaN(parsed) || parsed < 0) {
                            alert('Invalid price');
                            return;
                          }
                          updateItemSellPrice(item.product.id, parsed);
                        }}
                        title={item.adjustedPrice !== undefined && item.adjustedPrice !== null ? `Price overridden: Rs. ${formatRs(item.adjustedPrice)} (Click to edit)` : 'Override item price'}
                        className={`px-1.5 py-0.5 text-[8.5px] font-bold rounded flex items-center gap-1 transition-all ${
                          item.adjustedPrice !== undefined && item.adjustedPrice !== null
                            ? 'bg-blue-600 text-white border border-blue-700 shadow-xs'
                            : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        <span>💲</span>
                        <span>{item.adjustedPrice !== undefined && item.adjustedPrice !== null ? formatRs(item.adjustedPrice) : 'Override'}</span>
                      </button>
                      {item.adjustedPrice !== undefined && item.adjustedPrice !== null && (
                        <button
                          type="button"
                          onClick={() => updateItemSellPrice(item.product.id, null)}
                          title="Reset to default price"
                          className="w-4 h-4 rounded-full bg-slate-200 hover:bg-rose-100 hover:text-rose-600 text-slate-500 flex items-center justify-center text-[8.5px] transition-colors"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </SwipeableRow>
            );
          })}
        </AnimatePresence>
      </div>

      {/* ══ UNIFIED PRINT LAYER ══
           Renders ONLY on print (@media print). Hidden on screen.
      {/* ══ RECEIPT TEMPLATE FOR IMAGE CAPTURE ══
           Hidden off-screen. html2canvas captures this as PNG.
           Designed at 280px content width = 80mm thermal with proper margins. */}
      {lastTransaction && (() => {
        const configuredPrinter = getAgentConfig().defaultPrinter || '';
        const is4Inch = /(470|4-inch|4inch|104|108|100|label|barcode)/i.test(configuredPrinter);
        const receiptWidth = is4Inch ? '380px' : '280px';
        return (
          <div id="kangavena-receipt-print" className="kangavena-receipt-capture" style={{
            display: 'block', position: 'fixed', left: '-9999px', top: '0',
            width: receiptWidth, boxSizing: 'border-box', overflow: 'hidden'
          }}>
            <ReceiptTemplateRenderer
              templateId={receiptTemplate}
              data={{
                id: lastTransaction.id,
                storeName: storeName || lastTransaction.storeName,
                storeLogo: lastTransaction.storeLogo,
                storeAddress: lastTransaction.storeAddress,
                storePhone: lastTransaction.storePhone,
                time: lastTransaction.time,
                cashierName: cashierName || lastTransaction.cashierName,
                customer: lastTransaction.customer,
                cart: lastTransaction.cart,
                subtotal: lastTransaction.subtotal,
                discountPct: lastTransaction.discountPct,
                discountAmt: lastTransaction.discountAmt,
                storeCreditUsed: lastTransaction.storeCreditUsed,
                total: lastTransaction.total,
                payments: lastTransaction.payments,
                change: (lastTransaction.payments || []).find((p: any) => p.change !== undefined)?.change || lastTransaction.change || 0,
              }}
              is4Inch={is4Inch}
              showLogoOnBill={showLogoOnBill}
              hidePoweredByOnBill={hidePoweredByOnBill}
              isCapture={true}
            />
          </div>
        );
      })()}

      {/* Footer — pinned to bottom of sidebar so Charge / Confirm buttons are always 100% visible on all desktop resolutions */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-slate-100 p-3 space-y-2.5 shrink-0 z-20 print:hidden shadow-[0_-8px_16px_rgba(0,0,0,0.04)]">
        {lastTransaction ? (
          /* 🧾 START NEW SALE - Desktop */
          <div className="space-y-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
              <p className="text-[10px] text-emerald-600 font-bold">Sale Completed!</p>
              <p className="text-[16px] font-black text-emerald-700 font-mono mt-0.5">{formatRs(lastTransaction.total)}</p>
            </div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                if (onPrintThermalReceipt) {
                  setShowPrintChoiceModal(true);
                } else {
                  window.print();
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase tracking-wider bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25 active:scale-95 transition-all print:hidden"
            >
              🖨️ Print Receipt
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                setLastTransaction(null);
                setCart([]);
                setDiscount(0);
                setShowDiscount(false);
                setSidebarPayMode(false);
                setCashAmount('');
                setMobileProvider('');
              }}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[12px] uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 active:scale-95 transition-all print:hidden"
            >
              <CheckCircle2 size={16} />
              START NEW SALE
            </motion.button>

            {/* ══ REPRINT SELECTION MODAL ══ */}
            <AnimatePresence>
              {showPrintChoiceModal && lastTransaction && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                  onClick={() => setShowPrintChoiceModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 10 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 10 }}
                    className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden p-5 space-y-4"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center">
                          <Printer size={18} />
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-slate-800">Print Receipt</h3>
                          <p className="text-[10px] text-slate-500 font-mono">Bill #{lastTransaction.id} • {formatRs(lastTransaction.total)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPrintChoiceModal(false)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Select how you want to reprint this bill:
                    </p>

                    <div className="space-y-2.5">
                      {/* Option 1: Print via Smart Agent */}
                      <button
                        onClick={() => {
                          setShowPrintChoiceModal(false);
                          if (onPrintThermalReceipt) onPrintThermalReceipt();
                        }}
                        className="w-full text-left p-3.5 rounded-2xl border-2 border-emerald-500/30 bg-emerald-50/50 hover:bg-emerald-50 hover:border-emerald-500 transition-all flex items-start gap-3 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/20 group-hover:scale-105 transition-transform">
                          <Zap size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-black text-emerald-950">Print via Smart Print Agent</span>
                            <span className="text-[8px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">Direct</span>
                          </div>
                          <p className="text-[10px] text-emerald-700/80 mt-0.5 leading-snug">
                            Instant thermal print to Xprinter XP-470B / POS printer without browser dialog.
                          </p>
                        </div>
                      </button>

                      {/* Option 2: Print via Browser Dialog */}
                      <button
                        onClick={() => {
                          setShowPrintChoiceModal(false);
                          window.print();
                        }}
                        className="w-full text-left p-3.5 rounded-2xl border border-slate-200 bg-slate-50/80 hover:bg-slate-100 hover:border-slate-300 transition-all flex items-start gap-3 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-slate-200 text-slate-700 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                          <Globe size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-black text-slate-800">Print via Browser Dialog</div>
                          <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                            Opens default browser print page (for A4 paper, PDF export, or system printers).
                          </p>
                        </div>
                      </button>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={() => setShowPrintChoiceModal(false)}
                        className="w-full py-2.5 rounded-xl font-bold text-[11px] text-slate-500 hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : sidebarPayMode ? (
          /* 💳 Phase 10 — INLINE PAYMENT (Desktop) */
          <PaymentMethodPicker
            grandTotal={grandTotal}
            onConfirm={handlePaymentConfirm}
            onClose={() => setSidebarPayMode(false)}
            isProcessing={isProcessing}
            hasCustomer={!!customer}
            storeApiKey={storeApiKey}
            onCustomerAttached={onCustomerAttached}
            attachedCustomer={customer} // 🎯 Unified Customer Integration
          />
        ) : (
          <>
            {/* ✨ Silent Whisperer — AI upsell suggestion (background, expandable pill) */}
            <AnimatePresence>
              {aiSuggestion && (
                isSuggestionCollapsed ? (
                  <motion.div
                    key="collapsed-suggestion"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="relative flex items-center justify-between p-1.5 px-2.5 rounded-xl bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 border border-violet-200 shadow-xs text-[10.5px] mb-1.5 z-10"
                  >
                    <button
                      onClick={() => setIsSuggestionCollapsed(false)}
                      className="flex items-center gap-1.5 font-bold text-violet-900 hover:text-violet-950 transition-colors flex-1 text-left min-w-0"
                      title="Click to view suggestion details"
                    >
                      <Sparkles size={13} className="text-violet-600 shrink-0" />
                      <span className="truncate">
                        <span className="text-violet-500 font-semibold uppercase text-[9px] mr-1">Luna:</span>
                        <span className="font-bold text-slate-800">{aiSuggestion.item}</span>
                      </span>
                      <ChevronDown size={12} className="text-violet-500 shrink-0 ml-0.5" />
                    </button>
                    <div className="flex items-center gap-1 shrink-0 ml-1.5">
                      <button
                        onClick={() => handleAddSuggestedItem(aiSuggestion.item)}
                        className="px-2 py-0.5 bg-violet-600 hover:bg-violet-700 text-white text-[9px] font-bold rounded flex items-center gap-0.5 shadow-xs transition-colors"
                        title={`Add ${aiSuggestion.item}`}
                      >
                        <Plus size={10} /> Add
                      </button>
                      <button
                        onClick={() => {
                          setDismissedSuggestions(prev => new Set(prev).add(aiSuggestion.item));
                          setAiSuggestion(null);
                        }}
                        className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Dismiss"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={aiSuggestion.item}
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="relative p-2.5 rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50/60 border border-violet-200 shadow-sm overflow-hidden mb-1.5"
                  >
                    <div className="flex items-center justify-between pb-1 mb-1.5 border-b border-violet-100">
                      <div className="flex items-center gap-1 text-[9.5px] font-bold text-violet-700 uppercase tracking-wider">
                        <Sparkles size={12} className="text-violet-600" />
                        <span>Luna AI Suggestion</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setIsSuggestionCollapsed(true)}
                          className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-violet-700 hover:bg-violet-100 transition-colors"
                          title="Collapse"
                        >
                          <ChevronUp size={12} />
                        </button>
                        <button
                          onClick={() => {
                            setDismissedSuggestions(prev => new Set(prev).add(aiSuggestion.item));
                            setAiSuggestion(null);
                          }}
                          className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-100 transition-colors"
                          title="Dismiss"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-700 leading-snug mb-2 font-medium">{aiSuggestion.reason}</p>
                    <button
                      onClick={() => handleAddSuggestedItem(aiSuggestion.item)}
                      className="w-full flex items-center justify-center gap-1 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-xs"
                    >
                      <Plus size={11} /> Add {aiSuggestion.item}
                    </button>
                  </motion.div>
                )
              )}
            </AnimatePresence>

            {cart.length > 0 && (
              <div className="space-y-1 px-0.5 mb-2">
                <div className="flex justify-between items-center text-[11px] text-slate-500 font-bold font-mono">
                  <span>Subtotal</span>
                  <span>{formatRs(subtotal)}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="flex justify-between text-[11px] text-violet-600 font-bold font-mono">
                    <span>Discount {discount > 0 ? `(${discount}%)` : ''}</span>
                    <span>− {formatRs(discountAmt)}</span>
                  </div>
                )}
                {/* Inline Discount Presets */}
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Discount:</span>
                  {[5, 10, 15].map(d => (
                    <button key={d} onClick={() => { const newVal = discount === d ? 0 : d; setDiscount(newVal); }}
                      className={`px-2 py-1 rounded-lg text-[9px] font-black border transition-all ${
                        discount === d
                          ? 'bg-violet-500 border-violet-400 text-white'
                          : 'border-slate-200 text-violet-600 hover:bg-violet-50'
                      }`}>{d}%</button>
                  ))}
                  <button onClick={() => setShowDiscount(true)}
                    className="px-2 py-1 rounded-lg text-[9px] font-black border border-slate-200 text-slate-500 hover:bg-slate-50">
                    More
                  </button>
                </div>
                {/* 🏷️ Quick Apply Discount Button in Sidebar */}
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowDiscount(true)}
                    className="flex items-center gap-1 text-[11px] font-bold text-violet-600 hover:text-violet-800 transition-colors uppercase tracking-wider"
                  >
                    <Tag size={12} /> {discountAmt > 0 ? 'Change Discount' : 'Add Bill Discount'}
                  </button>
                </div>
              </div>
            )}

            {/* 🎯 Phase 3 — Inline Customer Search & Quick-Add (replaces modal) */}
            {!isCashierMode && cart.length > 0 && (
              <div className="mb-2">
                {customer ? (
                  /* ── Customer already attached ── */
                  <div className="rounded-xl bg-gradient-to-r from-amber-50 to-emerald-50 border border-amber-200 p-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white border border-amber-300 flex items-center justify-center shrink-0">
                        <User size={12} className="text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-black text-slate-800 truncate leading-tight">{customer.name}</div>
                        <div className="text-[9px] text-slate-500 font-mono truncate">{customer.phone}</div>
                      </div>
                      <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-black shrink-0">
                        <Award size={8} /> {Number(customer.points).toFixed(0)}
                      </div>
                      {Number(customer.storeCredit || 0) > 0 && (
                        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-black shrink-0">
                          💰 {formatRs(Number(customer.storeCredit))}
                        </div>
                      )}
                      <button
                        onClick={() => { onCustomerAttached?.(null as any); }}
                        className="w-6 h-6 rounded-md hover:bg-rose-100 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-colors shrink-0"
                        title="Remove customer"
                      >
                        <X size={11} />
                      </button>
                    </div>
                    {/* 💰 Redemption controls */}
                    {loyaltyConfig.enabled && Number(customer.points) > 0 && (
                      <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-amber-200/60">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700 shrink-0">Redeem</span>
                        <input
                          type="number"
                          min={0}
                          max={Math.min(Number(customer.points), Math.floor((totalBeforeRedemption || 0) / Math.max(loyaltyConfig.rupeePerPoint, 0.0001)))}
                          value={pointsToRedeem || ''}
                          onChange={(e) => {
                            const n = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
                            const maxByBalance = Math.floor(Number(customer.points));
                            const maxByBill = Math.floor((totalBeforeRedemption || 0) / Math.max(loyaltyConfig.rupeePerPoint, 0.0001));
                            setPointsToRedeem?.(Math.min(n, maxByBalance, maxByBill));
                          }}
                          placeholder="0"
                          className="flex-1 min-w-0 px-2 py-1 rounded-md bg-white border border-amber-200 outline-none focus:border-amber-400 text-[11px] font-mono font-bold text-slate-800 text-right tabular-nums"
                        />
                        <span className="text-[9px] font-mono text-slate-500 shrink-0">pts</span>
                        {(pointsToRedeem ?? 0) > 0 && (
                          <span className="text-[10px] font-black text-emerald-600 font-mono shrink-0">
                            −{formatRs(redemptionValue ?? 0)}
                          </span>
                        )}
                        {(pointsToRedeem ?? 0) === 0 && (
                          <button
                            onClick={() => {
                              const maxByBalance = Math.floor(Number(customer.points));
                              const maxByBill = Math.floor((totalBeforeRedemption || 0) / Math.max(loyaltyConfig.rupeePerPoint, 0.0001));
                              setPointsToRedeem?.(Math.min(maxByBalance, maxByBill));
                              Haptics.fire('tap');
                            }}
                            className="px-1.5 py-0.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-[8px] font-black uppercase tracking-wider transition-colors shrink-0"
                          >
                            Max
                          </button>
                        )}
                      </div>
                    )}
                    {/* Earn preview */}
                    {(estimatedPointsEarn ?? 0) > 0 && (
                      <div className="text-[9px] font-mono text-emerald-700 mt-1 text-center">
                        Earns <span className="font-black">+{(estimatedPointsEarn ?? 0).toFixed(0)} pts</span> on this bill
                      </div>
                    )}
                    {/* 💰 Store Credit */}
                    {Number(customer.storeCredit || 0) > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-emerald-200/60">
                        {storeCreditToUse > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold text-emerald-700 shrink-0">💰 Credit:</span>
                            <div className="flex-1 flex items-center gap-1 bg-emerald-50 border border-emerald-300 rounded-lg px-2 py-1">
                              <input
                                type="number"
                                min={0}
                                max={maxStoreCredit}
                                value={storeCreditToUse || ''}
                                onChange={(e) => {
                                  const n = Math.max(0, parseFloat(e.target.value || '0') || 0);
                                  setStoreCreditToUse?.(Math.min(n, maxStoreCredit));
                                }}
                                className="flex-1 w-14 px-1 py-0.5 rounded bg-white border border-emerald-300 text-[11px] font-mono font-black text-emerald-800 text-right outline-none"
                              />
                              <span className="text-[9px] font-mono text-emerald-600">Rs.</span>
                            </div>
                            <button
                              onClick={() => { setStoreCreditToUse?.(0); Haptics.fire('tap'); }}
                              className="w-6 h-6 rounded bg-rose-50 border border-rose-200 text-rose-500 text-[10px] flex items-center justify-center shrink-0"
                            >✕</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setStoreCreditToUse?.(maxStoreCredit); Haptics.fire('tap'); }}
                            className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-[11px] font-black uppercase tracking-wider shadow-sm active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
                          >
                            💰 Apply Store Credit — Rs. {maxStoreCredit.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : !customerSearchOpen ? (
                  /* ── Collapsed: Attach customer button ── */
                  <button
                    onClick={() => setCustomerSearchOpen(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-slate-300 hover:border-brand-primary text-slate-500 hover:text-brand-primary text-[10px] font-black uppercase tracking-widest transition-all"
                  >
                    <UserPlus size={12} /> Attach customer
                  </button>
                ) : (
                  /* ── Expanded: Search & Create ── */
                  <div className="space-y-2 bg-white rounded-xl border border-slate-200 p-2.5">
                    {/* Search input */}
                    <div className="relative">
                      <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={customerQuery}
                        onChange={(e) => setCustomerQuery(e.target.value)}
                        placeholder="Search by phone or name…"
                        className="w-full pl-8 pr-8 py-1.5 rounded-lg bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[11px] font-mono text-slate-800 placeholder:text-slate-400"
                        autoFocus
                      />
                      {customerLoading && <Loader2 size={10} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                    </div>

                    {/* Cancel / close row */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => { setCustomerSearchOpen(false); resetCustomerSearch(); }}
                        className="text-[9px] text-slate-400 hover:text-slate-600 font-medium"
                      >
                        ← Cancel
                      </button>
                      {customerQuery && !customerCreateMode && (
                        <button
                          onClick={() => {
                            setCustomerForm(f => ({ ...f, phone: looksLikePhone(customerQuery) ? customerQuery.trim() : '', name: !looksLikePhone(customerQuery) ? customerQuery.trim() : '' }));
                            setCustomerCreateMode(true);
                          }}
                          className="text-[9px] text-brand-primary font-bold hover:underline"
                        >
                          + New customer
                        </button>
                      )}
                    </div>

                    {!customerCreateMode ? (
                      /* ── Results list ── */
                      <div className="max-h-36 overflow-y-auto space-y-1">
                        {/* 🔥 Top Customers — shown when no search query */}
                        {!customerQuery && customerTopCustomers.length > 0 && (
                          <>
                            <div className="text-[8px] font-black uppercase text-amber-600 tracking-wider pb-0.5 pt-0.5 flex items-center gap-1">
                              <Crown size={9} /> වැඩිපුරම ගනුදෙනු කරන
                            </div>
                            {customerTopCustomers.map((c) => (
                              <button
                                key={`ctop-${c.id}`}
                                onClick={() => { onCustomerAttached?.(c); setCustomerSearchOpen(false); resetCustomerSearch(); }}
                                className="w-full flex items-center gap-2 p-2 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-amber-400 text-left transition-all"
                              >
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 border border-amber-300 flex items-center justify-center shrink-0">
                                  <Crown size={9} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-bold text-slate-800 truncate">{c.name}</div>
                                  <div className="text-[8px] text-slate-500 font-mono flex items-center gap-1">
                                    <Phone size={7} /> {c.phone}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="flex items-center gap-1 text-[9px] font-bold text-amber-500 justify-end">
                                    <Award size={8} /> {Number(c.points).toFixed(0)} pts
                                  </div>
                                  <div className="text-[8px] text-slate-400 font-mono text-right">
                                    {Number(c.totalVisits)}v
                                  </div>
                                </div>
                              </button>
                            ))}
                            <div className="border-t border-slate-100 pt-1"></div>
                          </>
                        )}

                        {customerTopLoading && !customerQuery && (
                          <div className="flex items-center justify-center py-2">
                            <Loader2 size={10} className="animate-spin text-amber-500" />
                          </div>
                        )}

                        {customerResults.length === 0 && customerQuery && !customerLoading && (
                          looksLikePhone(customerQuery) ? (
                            <button
                              onClick={() => {
                                setCustomerForm(f => ({ ...f, phone: customerQuery.trim() }));
                                setCustomerCreateMode(true);
                              }}
                              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-brand-primary/40 bg-brand-surface/40 text-brand-primary hover:bg-brand-surface text-[10px] font-black uppercase tracking-wider transition-all"
                            >
                              <UserPlus size={11} /> Create new customer
                            </button>
                          ) : (
                            <div className="text-center py-3 text-[10px] text-slate-400">No customers found</div>
                          )
                        )}
                        {customerResults.map(c => (
                          <button
                            key={c.id}
                            onClick={() => { onCustomerAttached?.(c); setCustomerSearchOpen(false); resetCustomerSearch(); }}
                            className="w-full flex items-center gap-2 p-2 rounded-lg border border-slate-100 bg-white hover:bg-brand-surface hover:border-brand-primary transition-all text-left"
                          >
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-primary-100 to-brand-surface border border-brand-surface-border flex items-center justify-center shrink-0">
                              <User size={10} className="text-brand-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] font-bold text-slate-800 truncate">{c.name}</div>
                              <div className="text-[8px] text-slate-500 font-mono flex items-center gap-1">
                                <Phone size={7} /> {c.phone}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="flex items-center gap-1 text-[9px] font-bold text-amber-500 justify-end">
                                <Award size={8} /> {Number(c.points).toFixed(0)} pts
                              </div>
                              <div className="text-[8px] text-slate-400 font-mono text-right">
                                {Number(c.totalVisits)}v
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      /* ── Create mode ── */
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <label className="text-[8px] font-black uppercase tracking-widest text-slate-500">Name *</label>
                          <input
                            type="text"
                            value={customerForm.name}
                            onChange={(e) => setCustomerForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Full name"
                            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[11px] text-slate-800"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black uppercase tracking-widest text-slate-500">Phone *</label>
                          <input
                            type="tel"
                            inputMode="tel"
                            value={customerForm.phone}
                            onChange={(e) => setCustomerForm(f => ({ ...f, phone: e.target.value }))}
                            placeholder="077 123 4567"
                            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[11px] font-mono text-slate-800"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black uppercase tracking-widest text-slate-500">Address</label>
                          <input
                            type="text"
                            value={customerForm.address}
                            onChange={(e) => setCustomerForm(f => ({ ...f, address: e.target.value }))}
                            placeholder="Optional"
                            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[11px] text-slate-800"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] font-black uppercase tracking-widest text-slate-500">{customFieldLabel}</label>
                          <input
                            type="text"
                            value={customerForm.customFieldValue}
                            onChange={(e) => setCustomerForm(f => ({ ...f, customFieldValue: e.target.value }))}
                            placeholder="Optional"
                            className="w-full px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[11px] text-slate-800"
                          />
                        </div>
                        {customerError && (
                          <div className="text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">{customerError}</div>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => setCustomerCreateMode(false)}
                            className="flex-1 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-wider transition-all"
                          >
                            Back
                          </button>
                          <button
                            onClick={submitNewCustomer}
                            disabled={customerSubmitting || !customerForm.name.trim() || !customerForm.phone.trim()}
                            className="flex-1 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[10px] font-black uppercase tracking-wider shadow-lg shadow-emerald-500/25 disabled:opacity-40 flex items-center justify-center gap-1"
                          >
                            {customerSubmitting ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                            Save &amp; Attach
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Cashier Selector */}
            {cart.length > 0 && (
              <div className="mb-2 px-1">
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-slate-50 border border-slate-200">
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider shrink-0">Cashier:</span>
                  <select
                    value={selectedCashier?.id || 0}
                    onChange={(e) => {
                      const id = parseInt(e.target.value, 10);
                      if (id === 0) {
                        setSelectedCashier({ id: 0, name: cashierName || 'Manager' });
                      } else {
                        const found = cashiersList.find(c => c.id === id);
                        if (found) {
                          setSelectedCashier({ id: found.id, name: found.name });
                        }
                      }
                    }}
                    className="flex-1 bg-transparent text-[11px] font-extrabold text-slate-800 outline-none cursor-pointer"
                  >
                    <option value={0}>{cashierName || 'Manager'}</option>
                    {cashiersList.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Grand Total */}
            <div className="rounded-2xl p-3 pos-card-shadow" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.9))', border: '1px solid rgba(30,27,75,0.08)' }}>
              <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.2em] mb-0.5">Grand Total</p>

              {/* 📋 Print Estimate button — ABOVE checkout per spec */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => { Haptics.fire('tap'); onSaveEstimate ? onSaveEstimate() : window.print(); }}
                disabled={cart.length === 0}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all duration-200 mb-2 print:hidden
                  ${cart.length === 0
                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                    : 'bg-[#1e1b4b]/5 hover:bg-[#1e1b4b]/10 text-[#1e1b4b] border border-[#1e1b4b]/15 hover:border-[#1e1b4b]/25'
                  }`}>
                🖨️ Save & Print Estimate
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.97 }}
                whileHover={cart.length > 0 ? { scale: 1.02 } : {}}
                onClick={() => { Haptics.fire('tap'); setSidebarPayMode(true); }}
                disabled={cart.length === 0}
                className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-black text-[12px] uppercase tracking-widest transition-all duration-200
                  ${cart.length === 0
                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                    : 'text-white border border-transparent shadow-xl'
                  }`}
                style={cart.length > 0 ? {
                  background: 'linear-gradient(135deg, #1e1b4b, #312e81, #4338ca)',
                  boxShadow: '0 8px 28px rgba(30,27,75,0.35), 0 0 0 1px rgba(30,27,75,0.2), inset 0 1px 0 rgba(255,255,255,0.15)',
                } : {}}
              >
                <CreditCard size={14} /> Charge {cart.length > 0 ? formatRs(grandTotal) : 'Bill'}
              </motion.button>

              <div className="flex items-center justify-between text-[7px] font-mono text-slate-400 pt-0.5">
                <div className="flex items-center gap-1"><Tag size={7} /> Kangavena POS.OS v2</div>
                <div>{now.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
